// src/components/SessionSidebar.tsx
import { useEffect, useState } from "react";
import { db } from "../firebase/firebase";
import {
  collection,
  onSnapshot,
  addDoc,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getAuth, signOut } from "firebase/auth";
import CustomAlert from "./CustomAlert";

export default function SessionSidebar({
  userId,
  onSelect,
  selectedSession,
  isPro,
}: {
  userId: string;
  selectedSession?: string | null;
  onSelect?: (id: string | null) => void;
  isPro: boolean;
}) {
  // ─── State ───────────────────────────────────────────────────────────────────
  const [sessions, setSessions] = useState<
    Array<{ id: string; name: string; agentId: string }>
  >([]);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>("");
  const [alerts, setAlerts] = useState<
    { id: number; message: string; type: "success" | "error" | "warning" }[]
  >([]);
  const [alertId, setAlertId] = useState(0);

  // Instead of a one‐time fetch, use onSnapshot so newly created agents show up immediately.
  const [userAgents, setUserAgents] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [choosingAgent, setChoosingAgent] = useState(false);
  const [agentToUse, setAgentToUse] = useState<string>("");

  const navigate = useNavigate();
  const { user } = useAuth();

  const showAlert = (
    message: string,
    type: "success" | "error" | "warning" = "error"
  ) => {
    const newAlert = { id: alertId, message, type };
    setAlerts((prev) => [...prev, newAlert]);
    setAlertId((prev) => prev + 1);
  };

  const closeAlert = (id: number) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  // ─── 1) Real‐time listener for “sessions” collection ────────────────────────────
  useEffect(() => {
    const sessionsRef = collection(db, `users/${userId}/sessions`);
    const unsub = onSnapshot(sessionsRef, (snap) => {
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as { name: string; agentId: string }),
      }));
      setSessions(list);
    });
    return () => unsub();
  }, [userId]);

  // ─── 2) Real‐time listener for “agents” collection so newly created agents appear immediately ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const agentsRef = collection(db, `users/${userId}/agents`);
    const unsub = onSnapshot(agentsRef, (snap) => {
      const list = snap.docs.map((d) => ({
        id: d.id,
        name: (d.data() as any).name,
      }));
      setUserAgents(list);
    });
    return () => unsub();
  }, [userId]);

  // ─── 3) Click “➕ New Session”: show warning if no agents, otherwise open dropdown overlay ─────────────────────────────────────────────────────────
  const createSession = () => {
    if (!userAgents.length) {
      showAlert(
        "You must create at least one agent before starting a session.",
        "warning"
      );
      return;
    }
    if (!isPro && sessions.length >= 3) {
      showAlert(
        "Free tier users are limited to 3 sessions. Upgrade to Pro to create more.",
        "warning"
      );
      return;
    }
    // Default to the first agent in the list
    setAgentToUse(userAgents[0]?.id || "");
    setChoosingAgent(true);
  };

  // ─── 4) Confirm “Create Session” with selected agent ───────────────────────────────
  const confirmCreateSession = async () => {
    if (!agentToUse) {
      showAlert("You must select an agent to start a session.", "error");
      return;
    }
    const chosenAgent = userAgents.find((a) => a.id === agentToUse)!;
    const sessionName = `Session: ${chosenAgent.name}`;

    try {
      // 4a) Create the session document with chosen agentId
      const docRef = await addDoc(collection(db, `users/${userId}/sessions`), {
        name: sessionName,
        createdAt: serverTimestamp(),
        agentId: chosenAgent.id,
      });

      // 4b) Increment sessionCount in usage/data
      const usageRef = doc(db, `users/${userId}/usage/data`);
      const usageSnap = await getDoc(usageRef);
      let sessionCount = 0;
      if (usageSnap.exists()) {
        sessionCount = (usageSnap.data() as any).sessionCount || 0;
      }
      await setDoc(
        usageRef,
        { sessionCount: sessionCount + 1 },
        { merge: true }
      );

      // 4c) Close overlay & navigate/select new session
      setChoosingAgent(false);
      if (typeof onSelect === "function") {
        onSelect(docRef.id);
      } else {
        navigate(`/runtime?sessionId=${docRef.id}`);
      }
    } catch (err) {
      console.error("Error creating session:", err);
      showAlert("Failed to create session. Please try again.", "error");
    }
  };

  const cancelChoosing = () => {
    setChoosingAgent(false);
  };

  const handleRename = async (sessionId: string) => {
    if (!editName.trim()) return;
    const sessionRef = doc(db, `users/${userId}/sessions/${sessionId}`);
    await updateDoc(sessionRef, { name: editName });
    setEditingSessionId(null);
  };

  const handleDelete = async (sessionId: string) => {
    // Remove session doc
    const sessionRef = doc(db, `users/${userId}/sessions/${sessionId}`);
    await deleteDoc(sessionRef);

    // Decrement usage/data sessionCount
    const usageRef = doc(db, `users/${userId}/usage/data`);
    try {
      const usageSnap = await getDoc(usageRef);
      let sessionCount = 0;
      if (usageSnap.exists()) {
        sessionCount = (usageSnap.data() as any).sessionCount || 0;
      }
      await setDoc(
        usageRef,
        { sessionCount: Math.max(0, sessionCount - 1) },
        { merge: true }
      );
    } catch (err) {
      console.error("Failed to decrement sessionCount:", err);
    }
  };

  const startEditing = (sessionId: string, currentName: string) => {
    setEditingSessionId(sessionId);
    setEditName(currentName);
  };

  // ─── 5) Selecting a session → navigate to updated URL so CortexRuntime picks it up ──────────────────────────────────────────────────────
  const handleSelectSession = (sessionId: string) => {
    navigate(`/runtime?sessionId=${sessionId}`);
  };

  const handleSignOut = async () => {
    const auth = getAuth();
    await signOut(auth);
    navigate("/signup");
  };

  return (
    <aside className="w-full bg-secondary border-r border-darkgray p-6 space-y-6 text-sm font-medium text-offwhite relative">
      {alerts.map((alert) => (
        <CustomAlert
          key={alert.id}
          message={alert.message}
          type={alert.type}
          onClose={() => closeAlert(alert.id)}
        />
      ))}

      {/* Logo / Home Link */}
      <Link
        to="/runtime"
        className="block text-xl font-bold text-offwhite hover:text-teal transition"
      >
        CortexRuntime
      </Link>

      {/* New Session Button */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Sessions</h2>
        <button
          onClick={createSession}
          className="w-full py-2 px-3 rounded-lg bg-teal hover:bg-teal/80 text-offwhite transition"
        >
          ➕ New Session
        </button>
      </div>

      {/* List of Sessions */}
      <div className="space-y-3 overflow-y-auto" style={{ maxHeight: "50vh" }}>
        {sessions.map((s) => (
          <div key={s.id} className="flex items-center gap-2">
            {editingSessionId === s.id ? (
              <>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={() => handleRename(s.id)}
                  onKeyPress={(e) => e.key === "Enter" && handleRename(s.id)}
                  className="flex-1 px-2 py-1 rounded-md border border-darkgray bg-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
                  autoFocus
                />
                <button
                  onClick={() => handleRename(s.id)}
                  className="text-teal hover:text-teal/80 transition"
                >
                  ✔
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleSelectSession(s.id)}
                  className={`flex-1 px-2 py-1 rounded-md text-sm text-left transition
                    ${
                      s.id === selectedSession
                        ? "bg-charcoal border border-darkgray"
                        : "bg-secondary hover:bg-charcoal"
                    }`}
                >
                  {s.name}
                </button>
                <button
                  onClick={() => startEditing(s.id, s.name)}
                  className="text-gray-400 hover:text-teal transition"
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDelete(s.id)}
                  className="text-gray-400 hover:text-red-500 transition"
                >
                  ✕
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Bottom Links */}
      <div className="pt-4 border-t border-darkgray space-y-2">
        <Link
          to="/library"
          className="block text-offwhite hover:text-teal transition"
        >
          My Library
        </Link>
        <Link
          to="/explore"
          className="block text-offwhite hover:text-teal transition"
        >
          Explore Agents
        </Link>
        <Link
          to="/billing"
          className="block text-offwhite hover:text-teal transition"
        >
          Billing Settings
        </Link>
        <Link
          to="/contact"
          className="block text-offwhite hover:text-teal transition"
        >
          Contact Us{" "}
        </Link>{" "}
        <Link
          to="/terms"
          className="block text-offwhite hover:text-teal transition"
        >
          Terms & Conditions{" "}
        </Link>{" "}
        <Link
          to="/privacy"
          className="block text-offwhite hover:text-teal transition"
        >
          Privacy Policy{" "}
        </Link>
        <button
          onClick={handleSignOut}
          className="block text-offwhite hover:text-teal transition"
        >
          Sign Out
        </button>
      </div>

      {/* Username Display */}
      {user && (
        <div className="pt-4 border-t border-darkgray text-sm text-gray-400">
          Logged in as:{" "}
          <span className="text-offwhite">
            {user.displayName || user.email}
          </span>
        </div>
      )}

      {/* ──────── Agent Picker Overlay ──────── */}
      {choosingAgent && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-secondary rounded-xl p-6 w-64 space-y-4 text-sm">
            <h3 className="text-lg font-semibold text-offwhite">
              Pick an Agent
            </h3>
            <select
              value={agentToUse}
              onChange={(e) => setAgentToUse(e.target.value)}
              className="w-full px-3 py-2 bg-charcoal text-offwhite border border-darkgray rounded-lg focus:outline-none focus:ring-2 focus:ring-teal/30 transition"
            >
              {userAgents.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                onClick={cancelChoosing}
                className="px-3 py-1 rounded-lg bg-gray-600 text-offwhite hover:bg-gray-700 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmCreateSession}
                className="px-3 py-1 rounded-lg bg-teal text-offwhite hover:bg-teal/80 transition"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ────────────────────────────────────── */}
    </aside>
  );
}
